/**
 * PracticeUploadModal — multi-phase upload wizard for masters publishing
 * a new VideoPractice. Extracted from CreatorModals.tsx (AMB-232 slice 3).
 * Behaviour byte-for-byte identical with the inline version that lived
 * there since the new-Dashboard rewrite.
 *
 * Phases: idle → resume_prompt (if an in-flight upload was interrupted) →
 * uploading → processing → success/error. Wired to api.chunkedUpload +
 * the uploadResume storage helper for byte-accurate resume.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    View,
    Text,
    Modal,
    TouchableOpacity,
    TextInput,
    ScrollView,
    ActivityIndicator,
    Platform,
    Image,
} from 'react-native';
import { ON_ACCENT_TEXT } from '../../styles/accentText';
import Ionicons from '@expo/vector-icons/Ionicons';

import { api, uploadMasterVideo, presignAndPutCover, cancelChunkedUpload } from '../../services/api';
import type { ChunkedUploadProgress } from '../../services/api';
import { friendlyApiError, isAuthError, shouldReportApiError } from '../../services/errorMessages';
import { captureError } from '../../utils/sentry';
import { uploadResume, type UploadResumeEntry } from '../../storage/uploadResume';
import type { MasterVideo } from '../../types/auth';
import { serializeDirections } from '../../constants/directions';
import { useDirections } from '../../hooks/useDirections';
import { buildToggleChipProps } from '../../utils/toggleChipA11y';
import { useAuth } from '../../context/AuthContext';
import { fontWeights } from '../../styles/typographyTokens';

import {
    ACCENT,
    formatBytes,
    formatThroughput,
    formatEta,
    fieldLabel,
    inputStyle,
    primaryBtn,
} from './creatorModalsShared';

/* ═══════════════════ Practice Upload Modal ═══════════════════ */

const VISIBILITY_OPTIONS = [
    { key: 'all', label: 'Все', icon: 'globe-outline' as const },
    { key: 'subscribers', label: 'Подписчики', icon: 'people-outline' as const },
    { key: 'private', label: 'Скрыто', icon: 'eye-off-outline' as const },
];

// CORE-167 — content-type switch. Audio accept mirrors the BE allowlist
// (mp3/wav/m4a, CORE-166's IsAllowedAudio); video keeps the broad picker.
const AUDIO_ACCEPT = '.mp3,.wav,.m4a,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac';
const CONTENT_TYPE_OPTIONS = [
    { key: 'video' as const, label: 'Видео', icon: 'videocam-outline' as const, formats: 'MP4, MOV, WEBM, AVI, MKV' },
    { key: 'audio' as const, label: 'Аудио', icon: 'musical-notes-outline' as const, formats: 'MP3, WAV, M4A' },
];

interface PracticeUploadModalProps {
    visible: boolean;
    onClose: () => void;
    onCreated?: (video: MasterVideo) => void;
    isDark: boolean;
    colors: any;
    /** Render the uploader as a dashboard card instead of a modal overlay. */
    inline?: boolean;
}

/**
 * Explicit upload phase — keys the UI so every state has its own copy,
 * controls, and disabled-button rules. See AMB-225 FE doc § 3 for the
 * full state machine.
 */
type UploadPhase =
    | { kind: 'idle' }
    | { kind: 'resume_prompt'; entry: UploadResumeEntry }
    | { kind: 'uploading' }
    | { kind: 'processing' }
    // CORE-415 — `authExpired` flags a dead session (token missing/rejected):
    // retrying can't recover it, so the error box offers «Войти снова» instead
    // of the dead-end «Повторить».
    | { kind: 'error'; message: string; authExpired?: boolean };

// Backend field caps (Agape.Shared/Models/ContentModels.cs: [MaxLength]). Enforced
// on input so a long title/description can't trip the API's validation — which the
// user only saw as a generic «Не удалось сохранить — проверьте введённые данные».
const TITLE_MAX = 500;
const DESCRIPTION_MAX = 2000;

export const PracticeUploadModal: React.FC<PracticeUploadModalProps> = ({
    visible, onClose, onCreated, isDark, colors: tc, inline = false,
}) => {
    const { user, logout } = useAuth();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [directions, setDirections] = useState<string[]>([]);
    const directionOptions = useDirections();
    const [visibility, setVisibility] = useState<'all' | 'subscribers' | 'private'>('all');
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoFileName, setVideoFileName] = useState<string | null>(null);
    // CORE-167 — «Видео / Аудио» switch at the top of the upload block. Audio
    // practices skip the S3 presign step (no audio presign on the BE) and ride
    // as a multipart AudioFile; the picked file is still held in `videoFile`.
    const [uploadType, setUploadType] = useState<'video' | 'audio'>('video');

    // CORE-3 — frame-grab cover picker. Mirrors the manifesto pattern in
    // DashboardScreen.handleCaptureManifestoFrame: scrub the picked clip in
    // a hidden <video> ref, draw the current frame to a canvas, hand both
    // a Blob (for upload) and a data URL (for the small preview tile) back.
    // Web-only — native uses the same `coverImage` field on createVideo
    // when/if a native frame-grab path is wired.
    const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(null);
    const [coverBlob, setCoverBlob] = useState<Blob | null>(null);
    const [coverDataUrl, setCoverDataUrl] = useState<string | null>(null);
    const videoElRef = useRef<HTMLVideoElement | null>(null);

    // Single source of truth for upload UI. Replaces the old soup of
    // `saving` + `progress` + `error` + `finalizeStatus` booleans.
    const [phase, setPhase] = useState<UploadPhase>({ kind: 'idle' });
    const [progressInfo, setProgressInfo] = useState<ChunkedUploadProgress | null>(null);
    // Sub-state of `processing` — assemble vs poll-pending-Xs. chunkedUpload
    // pings us on every attempt and on every second of the retry-backoff.
    const [finalizeStatus, setFinalizeStatus] = useState<
      | { phase: 'finalizing' }
      | { phase: 'pending'; secondsRemaining: number }
      | null
    >(null);
    // The session we'd hit on Retry — set right after `POST /init` returns
    // (via chunkedUpload's `onSessionStart` hook). Cleared on success.
    const activeResumeRef = useRef<{ sessionId: string; chunkSize: number; totalChunks: number } | null>(null);
    // CORE-181 — the S3 playback URL of an already-uploaded binary. Set once
    // chunkedUpload fully succeeds; lets Retry re-issue only the cheap
    // createVideo metadata POST instead of re-uploading the whole (multi-GB)
    // file when the *save* step is what hit a network blip. Cleared on a
    // successful save and on reset.
    const completedPlaybackUrlRef = useRef<string | null>(null);
    // CORE-315 — the S3 URL of an already-uploaded cover. Same idea as
    // completedPlaybackUrlRef: a createVideo retry must not re-upload the cover.
    const completedCoverUrlRef = useRef<string | null>(null);
    // In-flight chunked upload — aborted when the user closes the modal so
    // we don't leave a "ghost" upload running that lands somewhere weird
    // after the UI's gone.
    const uploadAbortRef = useRef<AbortController | null>(null);

    const isUploading = phase.kind === 'uploading';
    const isProcessing = phase.kind === 'processing';
    const isBusy = isUploading || isProcessing;

    useEffect(() => () => {
        uploadAbortRef.current?.abort();
    }, []);

    // Surface a stored resume slot on first open. Picking a slot doesn't
    // start the upload — we still need the user to re-select the same file
    // (the Blob can't survive a page reload), so the prompt opens the
    // picker on "Продолжить".
    useEffect(() => {
        if (!visible || !user?.id) return;
        let cancelled = false;
        (async () => {
            const stored = await uploadResume.get(user.id);
            if (cancelled) return;
            if (stored) setPhase({ kind: 'resume_prompt', entry: stored });
        })();
        return () => { cancelled = true; };
    }, [visible, user?.id]);

    const reset = () => {
        setTitle(''); setDescription(''); setDirections([]); setVisibility('all');
        setUploadType('video');
        setVideoFile(null); setVideoFileName(null);
        // Free the blob URL handed to the preview <video>. Skipping this on
        // every reset leaks memory across consecutive uploads.
        if (videoObjectUrl) {
            try { URL.revokeObjectURL(videoObjectUrl); } catch { /* noop */ }
        }
        setVideoObjectUrl(null);
        setCoverBlob(null);
        setCoverDataUrl(null);
        setPhase({ kind: 'idle' });
        setProgressInfo(null);
        setFinalizeStatus(null);
        activeResumeRef.current = null;
        completedPlaybackUrlRef.current = null;
        completedCoverUrlRef.current = null;
    };

    const toggleDirection = (d: string) => {
        setDirections((prev) => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    };

    const handleClose = () => {
        // Cancelling mid-upload is fine — sessionId stays valid for 24h on
        // the BE side so a future "Continue X?" prompt can pick it up.
        uploadAbortRef.current?.abort();
        uploadAbortRef.current = null;
        reset();
        onClose();
    };

    const pickFileFromBrowser = (accept = 'video/*'): Promise<File | null> => {
        return new Promise((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = accept;
            // CORE-314 — the file dialog must NOT be hidden with
            // `display:none` / `visibility:hidden`: Android Chrome & WebView
            // refuse to open the picker for an input that isn't laid out, so
            // the previous `display:none` fix left the button dead on Android
            // tablets. Keep the element rendered but pushed off-screen with
            // zero opacity instead — invisible to the user, but a real,
            // clickable element to the browser.
            input.style.position = 'fixed';
            input.style.top = '0';
            input.style.left = '0';
            input.style.width = '1px';
            input.style.height = '1px';
            input.style.opacity = '0';
            input.style.pointerEvents = 'none';
            const cleanup = () => { try { input.remove(); } catch { /* noop */ } };
            input.onchange = (e: any) => {
                const file: File | undefined = e.target.files?.[0];
                cleanup();
                resolve(file ?? null);
            };
            // Cancelling the OS picker fires no `change` event on most
            // Android browsers — listen for the window regaining focus to
            // tear the orphan input down so it can't pile up across taps.
            const onFocusBack = () => {
                setTimeout(() => {
                    // If `change` already fired, the input is gone — `isConnected`
                    // guards against resolving twice.
                    if (!input.isConnected) return;
                    // Mobile fix: on Android the `change` event can lag focus by
                    // well over 300 ms for a large video, so the old `resolve(null)`
                    // here silently dropped a real selection (no filename/preview).
                    // Read the picked file straight off the input instead — it's
                    // already populated once the OS picker closes; null only for a
                    // genuine cancel.
                    const picked = input.files?.[0] ?? null;
                    cleanup();
                    resolve(picked);
                }, 600);
                window.removeEventListener('focus', onFocusBack);
            };
            window.addEventListener('focus', onFocusBack);
            // Android Chrome/WebView silently ignore .click() on an <input>
            // that isn't in the DOM — the file dialog only opens for an
            // attached element. Desktop browsers tolerate the detached form,
            // which is why this worked everywhere except mobile.
            document.body.appendChild(input);
            input.click();
        });
    };

    // CORE-167 — switching content type clears the staged file: video and audio
    // accept different formats, and an audio file must never be presigned as a
    // video (and vice-versa). The form swaps live, no reload (ticket AC).
    const handleSelectUploadType = (type: 'video' | 'audio') => {
        if (type === uploadType) return;
        setUploadType(type);
        setVideoFile(null);
        setVideoFileName(null);
        if (videoObjectUrl) {
            try { URL.revokeObjectURL(videoObjectUrl); } catch { /* noop */ }
        }
        setVideoObjectUrl(null);
        setCoverBlob(null);
        setCoverDataUrl(null);
        if (phase.kind === 'error') setPhase({ kind: 'idle' });
    };

    const handlePickVideo = async () => {
        if (Platform.OS !== 'web') {
            setPhase({ kind: 'error', message: 'Загрузка доступна только в веб-версии.' });
            return;
        }
        const isAudio = uploadType === 'audio';
        const file = await pickFileFromBrowser(isAudio ? AUDIO_ACCEPT : 'video/*');
        if (file) {
            setVideoFile(file);
            setVideoFileName(file.name);
            // Revoke any prior preview URL so we don't leak across file swaps.
            if (videoObjectUrl) {
                try { URL.revokeObjectURL(videoObjectUrl); } catch { /* noop */ }
            }
            // Only video gets a scrubable preview + frame-grab cover. Audio has
            // no frame to grab, so no object URL / cover picker is created.
            setVideoObjectUrl(isAudio ? null : URL.createObjectURL(file));
            // Video derives its cover from a grabbed frame, so a new clip
            // invalidates the old cover. Audio's cover is a manually-uploaded
            // image (handlePickAudioCover), so it must survive (re)selecting the
            // audio file — otherwise picking cover-then-audio wipes it
            // (CORE-168 QA, Tima).
            if (!isAudio) {
                setCoverBlob(null);
                setCoverDataUrl(null);
            }
            // Picking a file clears any prior error so the user can retry
            // with a different file without first dismissing the banner.
            if (phase.kind === 'error') setPhase({ kind: 'idle' });
        }
    };

    // CORE-3 — frame grab. Draws videoElRef's current frame into a canvas
    // at the clip's native dimensions, then exports JPEG @ 0.92 quality:
    // dataUrl for the small in-form preview, Blob for the upload payload.
    // Identical mechanics to DashboardScreen.handleCaptureManifestoFrame
    // (which has been in prod since CORE-12).
    // CORE-168 — an audio practice has no video frame to grab, so its cover is
    // an uploaded image. Reuses coverBlob/coverDataUrl (the same fields the video
    // path uploads via presignAndPutCover); dataURL preview avoids a blob leak.
    const handlePickAudioCover = async () => {
        if (Platform.OS !== 'web') return;
        const img = await pickFileFromBrowser('image/jpeg,image/png,image/webp');
        if (!img) return;
        setCoverBlob(img);
        const reader = new FileReader();
        reader.onload = () => setCoverDataUrl(reader.result as string);
        reader.readAsDataURL(img);
    };

    const handleCaptureFrame = useCallback(async () => {
        if (Platform.OS !== 'web') return;
        const video = videoElRef.current;
        if (!video) return;
        if (!video.videoWidth) {
            window.alert('Видео ещё загружается. Дождитесь, пока появится первый кадр, и попробуйте снова.');
            return;
        }
        // CORE-315 — downscale the grabbed frame before exporting. The cover
        // rides inside the createVideo multipart POST (it's not on the S3
        // presigned path the video binary uses), and RU users' uploads to the
        // origin choke at ~128 KB (origin-IP bandwidth shaping). A native-res
        // frame from an iPad clip (1080p/4K) at q0.92 is several hundred KB to
        // multi-MB, so the metadata POST stalls mid-body — the "видео застывает
        // в загрузке" report. The cover is only ever shown as a thumbnail, so
        // we cap the long edge at 1280 px and step the JPEG quality down until
        // the blob fits well under the shaping threshold (~96 KB target, with
        // headroom for the small metadata + multipart overhead).
        const COVER_MAX_EDGE = 1280;
        const COVER_TARGET_BYTES = 96 * 1024;
        const srcW = video.videoWidth;
        const srcH = video.videoHeight;
        const scale = Math.min(1, COVER_MAX_EDGE / Math.max(srcW, srcH));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(srcW * scale));
        canvas.height = Math.max(1, Math.round(srcH * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const toBlobAsync = (quality: number): Promise<Blob | null> =>
                new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

            // Step quality down until the blob fits the budget; keep the
            // smallest we produced if even the lowest quality overshoots.
            const qualities = [0.72, 0.6, 0.5, 0.42];
            let best: Blob | null = null;
            for (const q of qualities) {
                const blob = await toBlobAsync(q);
                if (!blob) continue;
                if (!best || blob.size < best.size) best = blob;
                if (blob.size <= COVER_TARGET_BYTES) { best = blob; break; }
            }
            if (!best) {
                window.alert('Не удалось подготовить обложку. Попробуйте взять кадр ещё раз.');
                return;
            }
            setCoverBlob(best);
            // Preview from the same downscaled bytes — cheap and avoids holding
            // a second native-res data URI in state.
            const reader = new FileReader();
            reader.onload = () => setCoverDataUrl(typeof reader.result === 'string' ? reader.result : null);
            reader.readAsDataURL(best);
        } catch (err) {
            // SecurityError on tainted canvas — only possible if the source
            // came from a cross-origin URL. We feed it a blob: URL from the
            // master's own file pick, so this branch is defensive.
            console.warn('PracticeUpload: frame capture failed:', err);
            window.alert('Не удалось взять кадр из видео. Попробуйте загрузить файл заново.');
        }
    }, []);

    /**
     * Runs the chunkedUpload call + downstream createVideo. Shared by the
     * fresh-save and resume paths so the success / error handling stays in
     * one place. `resumeFrom` is undefined on a fresh upload.
     */
    const runUpload = async (
        file: File,
        // Resume is not supported on the presigned single-PUT path (CORE-232);
        // kept in the signature so the resume_prompt / retry callers compile.
        _resumeFrom?: { sessionId: string; chunkSize: number; totalChunks: number },
    ) => {
        setPhase({ kind: 'uploading' });
        setProgressInfo(null);
        setFinalizeStatus(null);

        const ctrl = new AbortController();
        uploadAbortRef.current = ctrl;

        try {
            // CORE-167 — audio practices have no presign path: the file rides as
            // a multipart AudioFile in the createVideo POST (BE forces
            // ContentType=audio). No S3 step, no cover frame-grab. Progress is
            // synthesised from the XHR percent against the known file size.
            if (uploadType === 'audio') {
                // CORE-168 — optional cover image. Presign it to S3 like the video
                // path so the create POST stays small; fall back to the inline
                // bytes if the presign/PUT fails.
                let audioCoverUrl: string | undefined;
                let audioCoverFallback: File | undefined;
                if (coverBlob) {
                    try {
                        audioCoverUrl = await presignAndPutCover(coverBlob, { signal: ctrl.signal });
                    } catch (e: any) {
                        if (e?.name === 'AbortError') throw e;
                        audioCoverFallback = new File([coverBlob], 'cover.jpg', { type: 'image/jpeg' });
                    }
                }
                const totalBytes = (file as File).size ?? 0;
                const result = await api.createVideo({
                    title: title.trim().slice(0, TITLE_MAX),
                    contentType: 'audio',
                    description: description.trim().slice(0, DESCRIPTION_MAX) || undefined,
                    category: serializeDirections(directions),
                    categories: directions.length > 0 ? [...directions] : undefined,
                    audioFile: file,
                    publishedAtUtc: new Date().toISOString(),
                    visibility,
                    ...(audioCoverUrl ? { coverImageUrl: audioCoverUrl } : audioCoverFallback ? { coverImage: audioCoverFallback } : {}),
                }, (percent) => {
                    setProgressInfo({
                        bytesUploaded: Math.round((totalBytes * percent) / 100),
                        bytesTotal: totalBytes,
                        percent,
                        throughputBytesPerSec: null,
                        etaSeconds: null,
                    });
                    if (percent >= 100) setPhase({ kind: 'processing' });
                }, ctrl.signal);

                if (user?.id) await uploadResume.clear(user.id).catch(() => {});
                onCreated?.(result);
                reset();
                onClose();
                return;
            }

            // Two-step: upload the binary to S3, then a metadata-only POST that
            // references the resulting S3 URL.
            // CORE-232 — the binary now goes straight to S3 via a presigned PUT,
            // bypassing the prod origin. RU users' uploads to the origin choke
            // at ~128 KB (origin-IP bandwidth shaping); S3 is not shaped. A
            // single PUT has no per-chunk resume, but it actually completes from
            // RU where the old chunked-to-origin path stalls. (Superseded the
            // chunkedUpload pipeline added in 096659d6.)
            // CORE-181 — if a previous attempt already pushed the binary to
            // S3 (createVideo was the step that failed), skip straight to the
            // metadata POST instead of re-uploading. Retrying a network blip
            // on save must not cost the user another multi-GB upload.
            let playbackUrl = completedPlaybackUrlRef.current;
            if (playbackUrl) {
                setPhase({ kind: 'processing' });
            } else {
                playbackUrl = await uploadMasterVideo(file, {
                    kind: 'practice',
                    fileName: file.name || 'video.mp4',
                    onProgress: (info) => setProgressInfo(info),
                    signal: ctrl.signal,
                });
                // Bytes are durably on S3 now — cache so a createVideo failure
                // below can be retried without re-uploading, and flip to the
                // indeterminate spinner while createVideo probes duration /
                // runs moderation.
                completedPlaybackUrlRef.current = playbackUrl;
                setPhase({ kind: 'processing' });
            }

            // CORE-72 — visibility now travels with createVideo (BE persists
            // it on the row, filters listings accordingly). The old hack of
            // posting then patching with setVideoVisibility is gone.
            // CORE-3 — wrap the grabbed Blob in a File so the createVideo
            // multipart layer sees a real filename and content-type. Master
            // can save without picking a cover (BE falls back to an auto-
            // generated thumbnail), which preserves the old happy path.
            // CORE-315 — push the cover to S3 (presigned) too, so createVideo
            // carries only a tiny URL string, not the cover bytes. The native-res
            // frame used to ride inside this multipart POST and choke the RU
            // origin's ~128 KB upload shaping (the video already dodges it via
            // S3). Cache the URL so a createVideo retry doesn't re-upload it. If
            // the presign/PUT fails, fall back to the inline (downscaled) cover —
            // strictly no worse than before.
            let coverImageUrl = completedCoverUrlRef.current ?? undefined;
            let coverFallback: File | undefined;
            if (!coverImageUrl && coverBlob) {
                try {
                    coverImageUrl = await presignAndPutCover(coverBlob, { signal: ctrl.signal });
                    completedCoverUrlRef.current = coverImageUrl;
                } catch (e: any) {
                    if (e?.name === 'AbortError') throw e;
                    console.warn('[PracticeUpload] cover presigned upload failed, falling back to inline', e);
                    coverFallback = new File([coverBlob], 'cover.jpg', { type: 'image/jpeg' });
                }
            }

            const result = await api.createVideo({
                title: title.trim().slice(0, TITLE_MAX),
                contentType: 'practice',
                description: description.trim().slice(0, DESCRIPTION_MAX) || undefined,
                category: serializeDirections(directions),
                categories: directions.length > 0 ? [...directions] : undefined,
                playbackUrl,
                publishedAtUtc: new Date().toISOString(),
                visibility,
                ...(coverImageUrl ? { coverImageUrl } : coverFallback ? { coverImage: coverFallback } : {}),
            // CORE-315 — pass the abort signal so the user can cancel during the
            // «processing» phase (this POST is a safe-to-abort metadata call: the
            // bytes are already durably on S3 and a cancelled create just leaves
            // an unreferenced object). The bounded timeout inside createVideo is
            // the backstop if the BE stalls.
            }, undefined, ctrl.signal);

            // Success path — clear the resume slot, fire the parent
            // callback, tear the modal down.
            if (user?.id) await uploadResume.clear(user.id).catch(() => {});
            onCreated?.(result);
            reset();
            onClose();
        } catch (e: any) {
            // User-initiated cancel — handleClose already tore down the
            // modal. The sessionId is kept on the BE for 24h so a future
            // "Continue X?" prompt can resume it.
            if (e?.name === 'AbortError') return;
            // CORE-308 — report the failure with enough context to triage:
            // which stage (S3 upload vs createVideo/processing) and the file
            // shape. `completedPlaybackUrlRef` being set means bytes already
            // reached S3, so the failure is in createVideo / server-side
            // processing rather than the upload itself.
            // CORE-1099 — 8 из 12 прод-событий этого потока за 90 дней —
            // собственная клиентская проверка размера («Файл слишком большой»,
            // UploadError со статусом 413), и она уже пишет свою телеметрию
            // `upload_size_rejected`. В Sentry это дубль, который лежит в одной
            // группе с настоящими сбоями S3. Отказы бэкенда (413/415/422)
            // человек тоже читает в модалке — остаются сеть и 5xx.
            if (shouldReportApiError(e)) {
                captureError(e, {
                    tags: {
                        flow: 'practice_upload',
                        stage: completedPlaybackUrlRef.current ? 'create_video' : 's3_upload',
                        status: e?.status,
                    },
                    extra: {
                        fileName: file?.name,
                        fileSizeBytes: file?.size,
                        contentType: 'practice',
                        titleLength: title.trim().length,
                        visibility,
                    },
                });
            }
            // CORE-205 — keep the raw technical reason in logs/Sentry, but
            // never show it in the UI (no "Chunk N failed", retry counts or
            // bare HTTP codes).
            console.error('[PracticeUpload] upload failed', e);
            // CORE-181 / CORE-205 — surface a human, Russian message.
            // friendlyApiError maps 413 → «Контент слишком большой…», TypeError →
            // «Нет соединения с сервером…» etc. The cached playbackUrl above
            // keeps Retry cheap.
            // CORE-415 — a dead session (token missing/rejected) can't be
            // recovered by retry; flag it so the error box offers re-login.
            setPhase({ kind: 'error', message: friendlyApiError(e), authExpired: isAuthError(e) });
        } finally {
            if (uploadAbortRef.current === ctrl) uploadAbortRef.current = null;
        }
    };

    const canSave =
        title.trim().length >= 3 && !!videoFile && directions.length > 0 && !isBusy;

    // Hint shown under a disabled save button — names exactly the gates the
    // user hasn't satisfied yet, so they don't have to guess why "Сохранить"
    // stays grey when they think everything is filled in. Empty title is the
    // common trap: it's at the top of the modal and scrolls off-screen.
    const missingFields: string[] = [];
    if (title.trim().length < 3) missingFields.push('название (мин. 3 символа)');
    if (directions.length === 0) missingFields.push('направление');
    if (!videoFile) missingFields.push(uploadType === 'audio' ? 'аудио' : 'видео');

    const handleSave = async () => {
        if (!canSave || !videoFile) return;
        await runUpload(videoFile, undefined);
    };

    /**
     * Pick the same file again and resume the persisted session against it.
     * `file.size` must match the stored entry — if the user picks a
     * different file, abandon the resume slot and treat it as a fresh
     * upload (otherwise the assembled file would be corrupt).
     */
    const handleResumeContinue = async (entry: UploadResumeEntry) => {
        if (Platform.OS !== 'web') {
            setPhase({ kind: 'error', message: 'Resume сейчас работает только в веб-версии.' });
            return;
        }
        const file = await pickFileFromBrowser();
        if (!file) return;
        if (file.size !== entry.fileSize) {
            setPhase({
                kind: 'error',
                message: `Выбранный файл не совпадает с сохранённым (${formatBytes(file.size)} vs ${formatBytes(entry.fileSize)}). Начните заново.`,
            });
            // A different file can never be assembled against this session's
            // chunk grid, so the session is dead — drop it on the server too.
            if (user?.id) await uploadResume.clear(user.id).catch(() => {});
            void cancelChunkedUpload(entry.sessionId);
            return;
        }
        setVideoFile(file);
        setVideoFileName(file.name);
        await runUpload(file, {
            sessionId: entry.sessionId,
            chunkSize: entry.chunkSize,
            totalChunks: entry.totalChunks,
        });
    };

    /**
     * Drop the resume entry and return to a clean Idle slate. The user declined
     * to continue, so the chunks already in S3 will never be assembled —
     * CORE-486: release them now instead of waiting on the worker's sweep.
     */
    const handleResumeCancel = async (entry: UploadResumeEntry) => {
        if (user?.id) await uploadResume.clear(user.id).catch(() => {});
        void cancelChunkedUpload(entry.sessionId);
        setPhase({ kind: 'idle' });
    };

    /**
     * Re-try after an Error. If chunkedUpload made it past `POST /init`
     * (we have a sessionId), resume the same session — that's the whole
     * point of the BE-side chunk tracking. Otherwise fall back to a
     * fresh upload using the still-picked videoFile.
     */
    const handleRetry = async () => {
        if (!videoFile) {
            setPhase({ kind: 'idle' });
            return;
        }
        await runUpload(videoFile, activeResumeRef.current ?? undefined);
    };

    const formFields = (
                    <>
                        {/* CORE-167 — content-type switch at the top of the upload
                            block. Swaps the file form (video vs audio) live, no
                            reload; the accepted-format hint updates with it. */}
                        <Text style={fieldLabel(tc.text) as any}>Тип контента</Text>
                        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20 }}>
                            {CONTENT_TYPE_OPTIONS.map((opt) => {
                                const active = uploadType === opt.key;
                                return (
                                    <TouchableOpacity
                                        key={opt.key}
                                        testID={`practice-type-${opt.key}`}
                                        onPress={() => handleSelectUploadType(opt.key)}
                                        disabled={isBusy}
                                        activeOpacity={0.85}
                                        style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 999, backgroundColor: active ? ACCENT : 'transparent', borderWidth: 1, borderColor: active ? ACCENT : (isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'), opacity: isBusy ? 0.5 : 1, ...(Platform.OS === 'web' && !isBusy && { cursor: 'pointer' } as any) } as any}
                                    >
                                        <Ionicons name={opt.icon} size={15} color={active ? ON_ACCENT_TEXT : tc.text} />
                                        <Text style={{ color: active ? ON_ACCENT_TEXT : tc.text, fontSize: 14, fontWeight: fontWeights.bold } as any}>{opt.label}</Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>

                        <Text style={fieldLabel(tc.text) as any}>
                            Название практики <Text style={{ color: ACCENT } as any}>*</Text>
                        </Text>
                        <TextInput value={title} onChangeText={(t) => setTitle(t.slice(0, TITLE_MAX))} maxLength={TITLE_MAX} placeholder="напр. Глубокая утренняя тишина" placeholderTextColor={tc.textMuted} style={[inputStyle(isDark, tc), { marginBottom: 6 }] as any} />
                        {/* CORE-372 — mirror the description's char counter on the title (Дмитрий's request). */}
                        <Text style={{ fontSize: 12, color: title.length >= TITLE_MAX ? ACCENT : tc.textMuted, textAlign: 'right', marginBottom: 20 } as any}>
                            {title.length} / {TITLE_MAX}
                        </Text>

                        <Text style={fieldLabel(tc.text) as any}>Описание</Text>
                        <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: -4, marginBottom: 8, lineHeight: 17 } as any}>
                            О чём практика, кому подойдёт, что взять с собой. Появится на странице практики и в карточке в ленте.
                        </Text>
                        <TextInput
                            value={description}
                            onChangeText={(t) => setDescription(t.slice(0, DESCRIPTION_MAX))}
                            maxLength={DESCRIPTION_MAX}
                            placeholder="Например: Мягкая утренняя последовательность для пробуждения тела и ума…"
                            placeholderTextColor={tc.textMuted}
                            multiline
                            style={[inputStyle(isDark, tc), { minHeight: 110, textAlignVertical: 'top', marginBottom: 6 }] as any}
                        />
                        <Text style={{ fontSize: 12, color: description.length >= DESCRIPTION_MAX ? ACCENT : tc.textMuted, textAlign: 'right', marginBottom: 20 } as any}>
                            {description.length} / {DESCRIPTION_MAX}
                        </Text>

                        <Text style={fieldLabel(tc.text) as any}>
                            Направления <Text style={{ color: ACCENT } as any}>*</Text>
                        </Text>
                        <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: -4, marginBottom: 8, lineHeight: 17 } as any}>
                            Выберите одно или несколько — практика появится во всех соответствующих фильтрах календаря.
                        </Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                            {directionOptions.map((d) => {
                                const active = directions.includes(d);
                                return (
                                    <TouchableOpacity
                                        key={d}
                                        onPress={() => toggleDirection(d)}
                                        {...buildToggleChipProps(Platform.OS, active, () => toggleDirection(d), d)}
                                        activeOpacity={0.85}
                                        style={{ paddingHorizontal: 16, paddingVertical: 10, borderRadius: 999, backgroundColor: active ? ACCENT : 'transparent', borderWidth: 1, borderColor: active ? ACCENT : (isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'), ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                    >
                                        <Text style={{ color: active ? ON_ACCENT_TEXT : tc.text, fontSize: 14, fontWeight: fontWeights.bold } as any}>{d}</Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>

                        <Text style={fieldLabel(tc.text) as any}>Кто видит</Text>
                        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20 }}>
                            {VISIBILITY_OPTIONS.map((opt) => {
                                const active = visibility === opt.key;
                                return (
                                    <TouchableOpacity
                                        key={opt.key}
                                        onPress={() => setVisibility(opt.key as any)}
                                        activeOpacity={0.85}
                                        style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 999, backgroundColor: active ? 'rgba(16,185,129,0.12)' : 'transparent', borderWidth: 1, borderColor: active ? '#10B981' : (isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'), ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                    >
                                        <Ionicons name={opt.icon} size={14} color={active ? '#10B981' : tc.text} />
                                        <Text style={{ color: active ? '#10B981' : tc.text, fontSize: 14, fontWeight: fontWeights.bold } as any}>{opt.label}</Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>

                        <Text style={fieldLabel(tc.text) as any}>
                            {uploadType === 'audio' ? 'Аудиофайл' : 'Видеофайл'} <Text style={{ color: ACCENT } as any}>*</Text>
                        </Text>
                        <TouchableOpacity testID="practice-pick-file" onPress={handlePickVideo} activeOpacity={0.85} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14, paddingHorizontal: 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(233, 130, 69,0.08)' : 'rgba(233, 130, 69,0.06)', borderWidth: 1, borderColor: 'rgba(233, 130, 69,0.25)', borderStyle: 'dashed', marginBottom: 8, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}>
                            <Ionicons name={uploadType === 'audio' ? 'musical-notes-outline' : 'cloud-upload-outline'} size={20} color={ACCENT} />
                            <Text style={{ flex: 1, fontSize: 15, color: videoFileName ? tc.text : ACCENT, fontWeight: '600' } as any} numberOfLines={1}>
                                {videoFileName || (uploadType === 'audio' ? 'Выберите аудио…' : 'Выберите видео…')}
                            </Text>
                        </TouchableOpacity>
                        {/* CORE-167 — accepted-format hint, matches the BE allowlist
                            per type so the restriction is visible before picking. */}
                        <Text testID="practice-format-hint" style={{ fontSize: 12, color: tc.textMuted, marginBottom: 12, lineHeight: 16 } as any}>
                            Форматы: {uploadType === 'audio' ? 'MP3, WAV, M4A' : 'MP4, MOV, WEBM, AVI, MKV'}
                        </Text>

                        {/* CORE-3 — cover-frame picker. Live <video> preview so
                            the master can scrub to a moment they want as the
                            poster, then click «Использовать текущий кадр» to
                            export that frame as the practice cover. Hidden until
                            a file is picked, web-only (HTMLVideoElement + canvas).
                            Comment from Tima on CORE-3: «Не создается превью для
                            видео. … кнопка как у нас используется на странице
                            настройки при загрузке манифеста — "Обложка" где можно
                            выбирать отрывок из видео». */}
                        {Platform.OS === 'web' && videoObjectUrl && (
                            <View style={{ marginBottom: 16, gap: 10 } as any}>
                                <Text style={fieldLabel(tc.text) as any}>Обложка из кадра</Text>
                                {/* Native <video> — RN-web passes through unknown
                                    tags as host components on the DOM side. We tag
                                    it `as any` because RN's TS types don't know
                                    about HTMLVideoElement props. */}
                                {React.createElement('video', {
                                    ref: videoElRef,
                                    src: videoObjectUrl,
                                    controls: true,
                                    preload: 'metadata',
                                    playsInline: true,
                                    style: {
                                        width: '100%',
                                        aspectRatio: '16 / 9',
                                        borderRadius: 12,
                                        backgroundColor: '#000',
                                    },
                                })}
                                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' } as any}>
                                    <View style={{ width: 168, aspectRatio: 16 / 9, borderRadius: 10, overflow: 'hidden', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)', alignItems: 'center', justifyContent: 'center' } as any}>
                                        {coverDataUrl ? (
                                            <Image source={{ uri: coverDataUrl }} style={{ width: '100%', height: '100%' } as any} />
                                        ) : (
                                            <View style={{ alignItems: 'center', gap: 4 } as any}>
                                                <Ionicons name="image-outline" size={18} color={tc.textMuted} />
                                                <Text style={{ fontSize: 10, color: tc.textMuted } as any}>Кадр не выбран</Text>
                                            </View>
                                        )}
                                    </View>
                                    <View style={{ flex: 1, minWidth: 150, gap: 6 } as any}>
                                        <TouchableOpacity
                                            onPress={handleCaptureFrame}
                                            activeOpacity={0.85}
                                            style={{
                                                flexDirection: 'row', alignItems: 'center', gap: 6,
                                                paddingHorizontal: 11, paddingVertical: 7, borderRadius: 9,
                                                alignSelf: 'flex-start',
                                                backgroundColor: ACCENT,
                                                ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                            } as any}
                                        >
                                            <Ionicons name="camera-outline" size={13} color={ON_ACCENT_TEXT} />
                                            <Text style={{ color: ON_ACCENT_TEXT, fontSize: 12, fontWeight: fontWeights.bold } as any}>
                                                Использовать текущий кадр
                                            </Text>
                                        </TouchableOpacity>
                                        <Text style={{ fontSize: 12, color: tc.textMuted, lineHeight: 16 } as any}>
                                            Перемотайте к моменту, который хотите видеть на превью, и нажмите кнопку.
                                            Без выбранного кадра обложка сгенерируется автоматически.
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        )}

                        {/* CORE-168 — audio cover. No video frame to grab, so the
                            master optionally uploads an image as the poster. */}
                        {Platform.OS === 'web' && uploadType === 'audio' && (
                            <View style={{ marginBottom: 16, gap: 10 } as any}>
                                <Text style={fieldLabel(tc.text) as any}>Обложка</Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' } as any}>
                                    <View style={{ width: 140, aspectRatio: 16 / 9, borderRadius: 10, overflow: 'hidden', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)', alignItems: 'center', justifyContent: 'center' } as any}>
                                        {coverDataUrl ? (
                                            <Image source={{ uri: coverDataUrl }} style={{ width: '100%', height: '100%' } as any} />
                                        ) : (
                                            <View style={{ alignItems: 'center', gap: 4 } as any}>
                                                <Ionicons name="image-outline" size={18} color={tc.textMuted} />
                                                <Text style={{ fontSize: 10, color: tc.textMuted } as any}>Не выбрана</Text>
                                            </View>
                                        )}
                                    </View>
                                    <View style={{ flex: 1, minWidth: 200, gap: 6 } as any}>
                                        <TouchableOpacity
                                            testID="practice-audio-cover-pick"
                                            onPress={handlePickAudioCover}
                                            activeOpacity={0.85}
                                            style={{
                                                flexDirection: 'row', alignItems: 'center', gap: 6,
                                                paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10,
                                                alignSelf: 'flex-start',
                                                backgroundColor: ACCENT,
                                                ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                            } as any}
                                        >
                                            <Ionicons name="image-outline" size={14} color={ON_ACCENT_TEXT} />
                                            <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}>
                                                Выберите изображение
                                            </Text>
                                        </TouchableOpacity>
                                        <Text style={{ fontSize: 12, color: tc.textMuted, lineHeight: 16 } as any}>
                                            Необязательно. JPG, PNG или WebP. Без обложки будет использован дефолт.
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        )}

                        {/* AMB-225 — resume banner. Surfaced when a prior
                            session is in localStorage and within the 24h
                            BE-side TTL. Picking «Продолжить» opens the file
                            picker so the user can re-attach the same Blob
                            (the browser can't restore it from storage). */}
                        {phase.kind === 'resume_prompt' && (
                            <View style={{
                                marginBottom: 12, padding: 14, borderRadius: 12,
                                backgroundColor: isDark ? 'rgba(233, 130, 69,0.10)' : 'rgba(233, 130, 69,0.08)',
                                borderWidth: 1, borderColor: 'rgba(233, 130, 69,0.25)',
                            } as any}>
                                <Text style={{ fontSize: 14, fontWeight: fontWeights.bold, color: tc.text, marginBottom: 4 } as any}>
                                    Незавершённая загрузка
                                </Text>
                                <Text style={{ fontSize: 13, color: tc.textMuted, marginBottom: 12 } as any} numberOfLines={2}>
                                    {phase.entry.fileName} · {formatBytes(phase.entry.fileSize)}
                                </Text>
                                <View style={{ flexDirection: 'row', gap: 8 }}>
                                    <TouchableOpacity
                                        onPress={() => handleResumeContinue(phase.entry)}
                                        activeOpacity={0.85}
                                        style={{
                                            flex: 1, paddingVertical: 10, borderRadius: 10, alignItems: 'center',
                                            backgroundColor: ACCENT,
                                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                        }}
                                    >
                                        <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}>
                                            Продолжить
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        onPress={() => handleResumeCancel(phase.entry)}
                                        activeOpacity={0.85}
                                        style={{
                                            paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10,
                                            borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)',
                                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                        }}
                                    >
                                        <Text style={{ color: tc.text, fontSize: 13, fontWeight: '600' } as any}>
                                            Начать заново
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        )}

                        {/* Uploading — byte-accurate progress + throughput
                            + ETA + Cancel. Throughput/ETA stay hidden until
                            the first 5s pacer window closes (chunkedUpload
                            reports `null` until then). */}
                        {isUploading && progressInfo && (
                            <View style={{ marginBottom: 12 }}>
                                <View style={{ height: 6, borderRadius: 3, backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                                    <View style={{ width: `${progressInfo.percent}%`, height: '100%', backgroundColor: ACCENT } as any} />
                                </View>
                                <View style={{ marginTop: 6, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                    <Text style={{ fontSize: 12, color: tc.textMuted } as any}>
                                        {formatBytes(progressInfo.bytesUploaded)} из {formatBytes(progressInfo.bytesTotal)}
                                        {progressInfo.throughputBytesPerSec !== null
                                            ? ` · ${formatThroughput(progressInfo.throughputBytesPerSec)}`
                                            : ''}
                                    </Text>
                                    {progressInfo.etaSeconds !== null && (
                                        <Text style={{ fontSize: 12, color: tc.textMuted } as any}>
                                            {formatEta(progressInfo.etaSeconds)}
                                        </Text>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Processing — `/complete` polling, no percent
                            (BE-side assembly is non-progressing for the FE). */}
                        {isProcessing && (
                            <View style={{ marginBottom: 12, padding: 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                <ActivityIndicator size="small" color={ACCENT} />
                                <Text style={{ flex: 1, fontSize: 13, color: tc.text } as any}>
                                    {finalizeStatus?.phase === 'finalizing'
                                        ? 'Обрабатываем видео… Это может занять до нескольких минут.'
                                        : finalizeStatus?.phase === 'pending'
                                        ? `Сервер собирает файл, проверю через ${finalizeStatus.secondsRemaining}с.`
                                        : 'Обрабатываем видео…'}
                                </Text>
                            </View>
                        )}

                        {/* Error — keeps Retry visible. Retry resumes the
                            same sessionId when possible (chunks already in
                            S3 don't re-upload). */}
                        {phase.kind === 'error' && (
                            <View style={{ marginBottom: 12, padding: 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(239,68,68,0.10)' : 'rgba(239,68,68,0.08)', borderWidth: 1, borderColor: 'rgba(239,68,68,0.25)' } as any}>
                                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                                    <Ionicons name="alert-circle" size={16} color="#EF4444" />
                                    <Text style={{ flex: 1, color: '#EF4444', fontSize: 13, lineHeight: 18 } as any}>
                                        {phase.message}
                                    </Text>
                                </View>
                                {/* CORE-415 — a dead session can't be retried;
                                    offer a single «Войти снова» that clears the
                                    ghost-auth state and routes to login. */}
                                {phase.authExpired ? (
                                    <TouchableOpacity
                                        onPress={() => { void logout(); }}
                                        activeOpacity={0.85}
                                        style={{
                                            paddingVertical: 10, borderRadius: 10, alignItems: 'center',
                                            backgroundColor: ACCENT,
                                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                        }}
                                    >
                                        <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}>
                                            Войти снова
                                        </Text>
                                    </TouchableOpacity>
                                ) : (
                                <View style={{ flexDirection: 'row', gap: 8 }}>
                                    <TouchableOpacity
                                        onPress={handleRetry}
                                        activeOpacity={0.85}
                                        style={{
                                            flex: 1, paddingVertical: 10, borderRadius: 10, alignItems: 'center',
                                            backgroundColor: ACCENT,
                                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                        }}
                                    >
                                        <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}>
                                            Повторить
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        onPress={() => {
                                            // Giving up after an error — the chunks are
                                            // orphaned from here on, so free them (CORE-486).
                                            const abandoned = activeResumeRef.current?.sessionId;
                                            activeResumeRef.current = null;
                                            if (user?.id) uploadResume.clear(user.id);
                                            if (abandoned) void cancelChunkedUpload(abandoned);
                                            setPhase({ kind: 'idle' });
                                        }}
                                        activeOpacity={0.85}
                                        style={{
                                            paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10,
                                            borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)',
                                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                        }}
                                    >
                                        <Text style={{ color: tc.text, fontSize: 13, fontWeight: '600' } as any}>
                                            Начать заново
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                                )}
                            </View>
                        )}

                        {/* Primary action — disabled while uploading /
                            processing (the resume / retry banners offer
                            their own buttons during those phases). */}
                        <View style={inline ? { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' } : undefined}>
                            <TouchableOpacity
                                onPress={handleSave}
                                disabled={!canSave}
                                activeOpacity={0.85}
                                style={[primaryBtn(!canSave, isDark), inline && { flexGrow: 1, minWidth: 150 }] as any}
                            >
                                {isBusy ? (
                                    <ActivityIndicator size="small" color="#FFF" />
                                ) : (
                                    <Text style={{ color: '#FFF', fontSize: 15, fontWeight: fontWeights.bold, letterSpacing: 0.5 } as any}>СОХРАНИТЬ ПРАКТИКУ</Text>
                                )}
                            </TouchableOpacity>
                            {inline && (
                                <Text style={{ flex: 1, minWidth: 130, color: tc.textMuted, fontSize: 12, lineHeight: 17 } as any}>
                                    Заполните поля для сохранения.
                                </Text>
                            )}
                        </View>

                        {/* Cancel button — available during Uploading AND
                            Processing (CORE-315). The create POST now takes the
                            abort signal, so cancelling during «processing» aborts
                            it cleanly: the bytes are already durably on S3 and a
                            cancelled create just leaves an unreferenced object —
                            no half-saved practice. Without this the user was
                            trapped on the spinner when the BE create stalled. */}
                        {isBusy && (
                            <TouchableOpacity
                                onPress={handleClose}
                                activeOpacity={0.85}
                                style={{
                                    marginTop: 10, paddingVertical: 12, borderRadius: 14, alignItems: 'center',
                                    borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)',
                                    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                }}
                            >
                                <Text style={{ color: tc.text, fontSize: 14, fontWeight: fontWeights.bold } as any}>Отмена</Text>
                            </TouchableOpacity>
                        )}

                        {!canSave && !isBusy && missingFields.length > 0 && phase.kind !== 'resume_prompt' && phase.kind !== 'error' && (
                            <Text style={{ marginTop: 8, fontSize: 12, color: tc.textMuted, textAlign: 'center' } as any}>
                                Чтобы сохранить, добавьте: {missingFields.join(', ')}.
                            </Text>
                        )}
                    </>
    );

    const header = (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 22, paddingTop: 22, paddingBottom: 12 }}>
            <Text style={{ fontSize: inline ? 20 : 22, fontWeight: fontWeights.bold, color: tc.text } as any}>
                {inline ? 'Загрузить практику' : 'Новая практика'}
            </Text>
            {!inline && (
                <TouchableOpacity onPress={handleClose} style={{ width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' }}>
                    <Ionicons name="close" size={22} color={tc.text} />
                </TouchableOpacity>
            )}
        </View>
    );

    if (inline) {
        return (
            <View style={{ width: '100%', backgroundColor: isDark ? '#101418' : '#FFFFFF', borderRadius: 18, borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                {header}
                <View style={{ paddingHorizontal: 22, paddingBottom: 22 }}>
                    {formFields}
                </View>
            </View>
        );
    }

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
            <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                <View style={{ width: '100%', maxWidth: 520, maxHeight: '92%', backgroundColor: isDark ? '#162130' : '#FFFFFF', borderRadius: 22, overflow: 'hidden', ...(Platform.OS === 'web' && { boxShadow: '0 20px 60px rgba(0,0,0,0.25)' } as any) }}>
                    {header}
                    <ScrollView style={{ paddingHorizontal: 22 }} contentContainerStyle={{ paddingBottom: 22 }}>
                        {formFields}
                    </ScrollView>
                </View>
            </View>
        </Modal>
    );
};

export default PracticeUploadModal;
